// -----------------------------------------------------------------------------
// Libraries
// -----------------------------------------------------------------------------
import React, { useCallback } from "react";
import cx from "classnames";
import { Form, Modal, notification } from "antd";
import { validateRequiredEncodedLength } from "helpers/Form/commonFormValidations";
// -----------------------------------------------------------------------------
// Components and helpers
// -----------------------------------------------------------------------------
import { Button } from "components/Common/Button";
import { FormInput } from "components/Common/Form/FormInput";
import { FormSelect } from "components/Common/Form/FormSelect";
import { Heading2, Paragraph12 } from "themes/default/_typography";

// -----------------------------------------------------------------------------
// Actions and styles
// -----------------------------------------------------------------------------
import { useCreateLeadMutation } from "store/api/api";
import InputField from "components/UI/Form/InputField";

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------
const ContactSalesModal = ({ open, setOpen, className }) => {
	const [createLeadTrigger, { isLoading }] = useCreateLeadMutation();

	const onFinishForm = useCallback(
		async (values) => {
			const phoneNumber = `+${values.phone?.countryCode}${values.phone?.areaCode || ""}${
				values.phone?.phoneNumber || ""
			}`;

			const response = await createLeadTrigger({
				first_name: values.firstName,
				last_name: values.lastName,
				email_address: values.email,
				phone_number: phoneNumber,
				business_type: values.businessType,
				member_count: values.memberCount,
			});
			if (response?.error) {
				notification.error({
					message: response?.error?.data?.error || "Error creating lead. Please try again later.",
				});
			} else {
				notification.success({
					message: "Lead created successfully. We will contact you soon.",
				});
				setOpen(false);
			}
		},
		[setOpen, createLeadTrigger]
	);

	const validator = (_, { valid }) => {
		if (valid(true)) return Promise.resolve(); // strict validation
		return Promise.reject("Invalid phone number");
	};
	return (
		<Modal
			className={cx(className, "sb-contact-sales-modal")}
			title={<Heading2>Contact Sales</Heading2>}
			centered
			open={open}
			onOk={() => setOpen(false)}
			onCancel={() => setOpen(false)}
			width={656}
			footer={null}
			maskClosable={false}
			destroyOnClose
		>
			<div className="sb-contact-sales-modal__subtitle">
				<Paragraph12>
					Fill out the form below and one of our SweatBase experts will advise you on the best solutions to manage your
					business.
				</Paragraph12>
			</div>
			<div className="sb-contact-sales-modal__form">
				<Form
					initialValues={{
						firstName: "",
						lastName: "",
						email: "",
						phone: { countryCode: "1", areaCode: "", phoneNumber: "" },
						businessType: "",
						memberCount: "",
					}}
					onFinish={onFinishForm}
					layout="vertical"
				>
					<div className="sb-contact-sales-modal__form__inline">
						<FormInput
							name="firstName"
							label="First Name"
							rules={[
								() => ({
									required: true,
									validator(_, value) {
										const validationStatus = validateRequiredEncodedLength(value, 50);
										if (validationStatus) {
											return Promise.reject(new Error(validationStatus));
										}
										return Promise.resolve();
									},
								}),
							]}
						/>
						<FormInput
							name="lastName"
							label="Last Name"
							rules={[
								() => ({
									required: true,
									validator(_, value) {
										const validationStatus = validateRequiredEncodedLength(value, 50);
										if (validationStatus) {
											return Promise.reject(new Error(validationStatus));
										}
										return Promise.resolve();
									},
								}),
							]}
						/>
					</div>
					<FormInput
						name="email"
						label="Email Address"
						rules={[{ type: "email" }, { required: true, message: "Field Required" }]}
					/>
					<Form.Item
						name="phone"
						label="Phone Number"
						rules={[{ validator }, { required: true, message: "Field Required" }]}
					>
						<InputField className="" type="phone" required />
					</Form.Item>
					<div className="sb-contact-sales-modal__form__inline">
						<FormSelect
							name="businessType"
							label="Type of business"
							placeholder="Type of business"
							rules={[{ required: true, message: "Field Required" }]}
							options={[
								{ value: "trainer", label: "Personal Trainer" },
								{ value: "independent", label: "Independent Gym" },
								{ value: "corporate", label: "Corporate Gym" },
								{ value: "franchise", label: "Gym Franchise" },
							]}
						/>
						<FormSelect
							name="memberCount"
							label="Member Count"
							placeholder="Select member count"
							rules={[{ required: true, message: "Field Required" }]}
							options={[
								{ value: "1-100", label: "From 1 to 100 members" },
								{ value: "101-300", label: "From 101 to 300 members" },
								{ value: "301-1000", label: "From 301 to 1,000 members" },
								{ value: "1000+", label: "Over 1,000 members" },
							]}
						/>
					</div>
					<Form.Item>
						<Button className="primary-filled" type="submit" loading={isLoading} uppercase>
							{"SUBMIT FORM & CONTINUE"}
						</Button>
					</Form.Item>
				</Form>
			</div>
		</Modal>
	);
};

export default ContactSalesModal;

